/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { DeletableThumbnailBadge } from '../../../../components/DeletableThumbnailBadge/DeletableThumbnailBadge';
import { DescriptionPanel } from '../../../../components/DescriptionPanel/DescriptionPanel';
import { EntityThumbnail } from '../../../../components/EntityThumbnail/EntityThumbnail';
import { useUpdateProject } from '../../../../components/hooks/useUpdateProject';
import { SimpleConfirmModal } from '../../../../components/SimpleConfirmModal/SimpleConfirmModal';
import { SimpleNotifyModal } from '../../../../components/SimpleNotifyModal/SimpleNotifyModal';
import { Namespaces } from '../../../../i18n';
import { ProjectApiService } from '../../../../services/ProjectApiService';
import { RootState } from '../../../../store';
import { ApiClientError } from '../../../../types/api/ApiClientError';
import { EntityId } from '../../../../types/api/EntityId';
import { IProject } from '../../../../types/project/IProject';
import { UpdateProjectRequestBody } from '../../../../types/project/UpdateProjectRequestBody';
import { readFile } from '../../../../utils/canvasUtils';
import { reactToUpdateProjectThumbnail } from '../../../../utils/reactions';
import { useIsMounted } from '../../../../utils/useIsMounted';
import { useProjectPermissions } from '../../../../utils/useProjectPermissions';
import { createProjectPageHeaderStyles } from './ProjectPageHeader.styles';

const mapStateToProps = (state: RootState) => ({
  organizationDetails: state.organizationDetails,
});
const connector = connect(mapStateToProps);

type ProjectPageHeaderProps = {
  project: IProject | null;
  isFetching: boolean;
  organizationId: EntityId;
  projectId: EntityId;
  handleManageClick: () => void;
  onChangeProject: (e: boolean) => void;
  onDeleteProject: () => void;
};

const ProjectPageHeaderComponent = memo<ProjectPageHeaderProps>(props => {
  const classes = createProjectPageHeaderStyles();
  const { t } = useTranslation(Namespaces.Common);
  const { project, isFetching, organizationId, projectId, handleManageClick, onChangeProject, onDeleteProject } = props;

  const permissions = useProjectPermissions(organizationId, projectId);

  const isMounted = useIsMounted();

  const [isOpenErrorModal, setIsOpenErrorModal] = useState<boolean>(false);

  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [isOpenDelModal, setIsOpenDelModal] = useState<boolean>(false);

  const uploadThumbnail = async (thumbnail: Blob) => {
    if (thumbnail && permissions.edit_project) {
      setIsDisabled(true);
      try {
        await ProjectApiService.updateProjectThumbnail({
          projectId,
          organizationId,
          body: { base64: await readFile(thumbnail) },
        });
        await reactToUpdateProjectThumbnail({
          organizationId,
          projectId,
        });
      } catch (e) {
        console.error(e);
        setIsOpenErrorModal(true);
      } finally {
        setIsDisabled(false);
      }
    }
  };

  const [updateProject] = useUpdateProject(true);

  const onEditSubmit = async (values: UpdateProjectRequestBody) => {
    if (project) {
      try {
        const data: UpdateProjectRequestBody = {};
        if (values.name !== undefined) {
          data.name = values.name.trim();
        }
        if (values.description !== undefined) {
          data.description = values.description?.trim() || null;
        }

        await updateProject({ organizationId, projectId, data });
        if (!isMounted.current) {
          return;
        }
        onChangeProject(true);
      } catch (e) {
        if (!isMounted.current) {
          return;
        }
        const error = e as ApiClientError;
        console.error(error);
      }
    }
  };

  const onConfirmDeleteImage = async () => {
    setIsOpenDelModal(false);
    setIsDisabled(true);
    try {
      await ProjectApiService.deleteProjectThumbnail({
        organizationId,
        projectId,
      });
      await reactToUpdateProjectThumbnail({
        organizationId,
        projectId,
      });
    } catch (e) {
      const error = e as ApiClientError;
      console.log(error);
      setIsOpenErrorModal(true);
    } finally {
      setIsDisabled(false);
    }
  };

  const hideDeleteAvatarBtn = useMemo(() => !project?._links?.thumbnail, [project]);

  return (
    <div className={classes.root}>
      <DescriptionPanel
        name={project?.name || ''}
        description={project?.description || null}
        icon={
          <DeletableThumbnailBadge
            overlap="rectangular"
            invisible={hideDeleteAvatarBtn}
            onDelBtnClick={() => setIsOpenDelModal(true)}
            disabled={isDisabled}
            thumbnail={
              <EntityThumbnail
                size={54}
                loaderSize={34}
                type="project"
                organizationId={organizationId}
                projectId={projectId}
                {...(permissions.edit_project
                  ? { allowUploadImage: true, onImageUpload: uploadThumbnail }
                  : { allowUploadImage: false })}
              />
            }
          />
        }
        isLoading={isFetching}
        isEditable={permissions.edit_project}
        onSubmit={onEditSubmit}
        handleManage={handleManageClick}
        isDeletable={permissions.delete_project}
        handleDelete={onDeleteProject}
      />
      {permissions.delete_project && (
        <>
          <SimpleNotifyModal
            open={isOpenErrorModal}
            message={t(`${Namespaces.Common}:errors.unexpected_error`)}
            onOk={() => setIsOpenErrorModal(false)}
          />
          <SimpleConfirmModal
            open={isOpenDelModal}
            message={t(`${Namespaces.OrganizationAdmin}:projects.action_delete_thumbnail.content_text`)}
            onCancel={() => setIsOpenDelModal(false)}
            onConfirm={onConfirmDeleteImage}
          />
        </>
      )}
    </div>
  );
});

export const ProjectPageHeader = connector(ProjectPageHeaderComponent);
